import { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { initializeFirebase, functions } from './firebase';
import './App.css';
import './styles/buttons.css';
import './styles/forms.css';
import './styles/utilities.css';
import { Toaster } from 'react-hot-toast';
import './styles/EditDuck.css';
import './styles/DuckProfile.css';
import './styles/DuckPreview.css';
import './styles/DuckAdventure.css';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import Header from './components/Header';
import mountains from './assets/mountains.jpg';
import pond from './assets/pond.png';
import myducks from './assets/myducks.png';
import { httpsCallable } from 'firebase/functions';
import { DebugPanel } from './dev/DebugPanel';
import RewardProvider from './providers/RewardProvider';
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import PageTransition from './components/PageTransition';
import DailyTokenModal from './components/DailyTokenModal';
import LevelUpModal from './components/LevelUpModal';
import OfflineBanner from './components/OfflineBanner';
import GoogleProfileFinisherGate from './components/GoogleProfileFinisherGate';
import { usePremiumStatus } from './hooks/usePremiumStatus';
import { AppRoutes } from './routes';
import { SearchProvider } from './contexts/SearchContext';
import { useAuth } from './context/AuthContext';

function App() {
  const { user, userDoc, loading } = useAuth();
  const [error, setError] = useState(null);
  const [dailyReward, setDailyReward] = useState({ open: false, nextEligibleAt: null });
  const [levelUp, setLevelUp] = useState({ open: false, newLevel: null });
  const postLoginInitRef = useRef({ uid: null, ran: false });
  const prevLevelRef = useRef(null);
  const { isPremium, loading: premiumLoading } = usePremiumStatus(user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(min-width: 992px)').matches) {
      [mountains, pond, myducks].forEach((src) => { const img = new Image(); img.src = src; });
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      if (postLoginInitRef.current.uid === user.uid && postLoginInitRef.current.ran) return;

      try {
        await user.getIdToken();
      } catch (err) {
        console.debug('Skipping post-login init: failed to get ID token yet', err);
        return;
      }

      postLoginInitRef.current = { uid: user.uid, ran: true };

      try {
        const initAccount = httpsCallable(functions, 'initializeUserAccount');
        await initAccount();
      } catch (err) {
        console.debug('initializeUserAccount failed', err);
      }

      try {
        const claimDaily = httpsCallable(functions, 'claimDailyToken');
        const res = await claimDaily();
        if (!cancelled && res?.data?.awarded) {
          setDailyReward({
            open: true,
            nextEligibleAt: res?.data?.nextEligibleAt || null
          });
        }
      } catch (err) {
        console.debug('claimDailyToken failed', err);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user) {
      prevLevelRef.current = null;
      return;
    }
    const stats = userDoc?.stats || {};
    const currentLevel = Number(stats.level || 1);

    if (prevLevelRef.current !== null && currentLevel > prevLevelRef.current) {
      setLevelUp({ open: true, newLevel: currentLevel });
    }
    prevLevelRef.current = currentLevel;
  }, [user, userDoc]);

  useEffect(() => {
    if (!initializeFirebase()) {
      setError(new Error('Failed to initialize Firebase'));
    }
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="app-error-container" role="alert">
      <h1>Something went wrong</h1>
      <p>We couldn't start the app. Please try reloading the page.</p>
      <button onClick={() => window.location.reload()} className="btn btn-orange">
        Reload Page
      </button>
    </div>
  );

  return (
    <ErrorBoundary>
      <SearchProvider>
      <RewardProvider>
        <div className="App">
          <DailyTokenModal
            isOpen={dailyReward.open}
            nextEligibleAt={dailyReward.nextEligibleAt}
            onClose={() => setDailyReward({ open: false, nextEligibleAt: dailyReward.nextEligibleAt })}
          />
          <LevelUpModal
            isOpen={levelUp.open}
            newLevel={levelUp.newLevel}
            onClose={() => setLevelUp({ open: false, newLevel: null })}
          />
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <a href="#main-content" className="skip-link">Skip to content</a>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#333',
                  color: '#fff',
                },
              }}
            />
            <OfflineBanner />
            <Header user={user} mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen} />
            <GoogleProfileFinisherGate />
            <main id="main-content">
              <PageTransition>
                <Suspense fallback={<LoadingSpinner />}>
                  <AppRoutes user={user} isPremium={isPremium} premiumLoading={premiumLoading} />
                </Suspense>
              </PageTransition>
            </main>
            <Footer />
            <BottomNav
              user={user}
              onToggleMenu={() => setMobileMenuOpen((o) => !o)}
            />
          </Router>
        </div>
        <DebugPanel />
      </RewardProvider>
      </SearchProvider>
    </ErrorBoundary>
  );
}

export default App;
